import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import TaxPayer, {
  TaxPayerSuspensionSummary,
} from "../../models/tax-payer";
import { LOG_OUT } from "../reducers/user";

export interface TaxPayerState {
  loaded: boolean;
  loading: Boolean;
  error: boolean;
  errorDetails: string[];
  data: {
    taxPayer: TaxPayer;
    userTaxPayers: TaxPayer[];
  };
}

const initialState: TaxPayerState = {
  loaded: false,
  loading: false,
  error: false,
  errorDetails: [],
  data: {
    taxPayer: {},
    userTaxPayers: [],
  },
};

export const taxPayerSlice = createSlice({
  name: "tax-payer",
  initialState,
  reducers: {
    setTaxPayerLoaded: (state, action: PayloadAction<boolean>) => {
      state.loaded = action.payload;
    },
    setTaxPayerLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setTaxPayer: (state, action: PayloadAction<TaxPayer>) => {
      state.loaded = true;
      state.loading = false;
      state.data.taxPayer = action.payload;
    },
    setUserTaxPayers: (state, action: PayloadAction<TaxPayer[]>) => {
      state.data.userTaxPayers = action.payload;
    },
    setTaxPayerErrorLoading: (state, action: PayloadAction<boolean>) => {
      state.error = action.payload;
    },
    // The server refused a request because the company was suspended while
    // the app was open.
    markTaxPayerSuspended: (
      state,
      action: PayloadAction<{
        taxPayerId: number;
        suspension: TaxPayerSuspensionSummary;
      }>,
    ) => {
      const { taxPayerId, suspension } = action.payload;
      // Leaving an already blocked company untouched keeps its object
      // identity, so nothing that depends on it reloads and gets refused again.
      const mark = (taxPayer: TaxPayer) => {
        if (taxPayer.id === taxPayerId && taxPayer.accessBlocked !== true) {
          taxPayer.suspension = suspension;
          taxPayer.accessBlocked = true;
        }
      };
      mark(state.data.taxPayer);
      state.data.userTaxPayers.forEach(mark);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(LOG_OUT, () => initialState);
  },
});

export const {
  setTaxPayerLoaded,
  setTaxPayerLoading,
  setTaxPayer,
  setUserTaxPayers,
  setTaxPayerErrorLoading,
  markTaxPayerSuspended,
} = taxPayerSlice.actions;

export default taxPayerSlice.reducer;
